import { AggregatorQuote, QuoteProvider, QuoteRequest, RouteHop } from '../types'
import { buildQuery, endpointFor, getJson, hasQuoteProxy } from '../http'
import { revenueConfig } from '../revenue'

/**
 * 0x Swap API (v2, allowance-holder flow).
 *
 * Revenue rides on `swapFeeRecipient` / `swapFeeBps` / `swapFeeToken`. 0x takes the cut
 * at settlement and forwards it, so no fee-collection contract of our own is needed.
 *
 * Requires an API key, which must live on the proxy - never in the bundle. When no
 * proxy is configured this provider reports itself unconfigured and the registry skips
 * it, rather than failing every quote with a 401.
 *
 * FIELD NAMES: verify `normalise` against a live response when the key is issued.
 * All 0x-specific parsing is confined to that function.
 */

const DIRECT_BASE = 'https://api.0x.org'

const SUPPORTED_CHAINS = [56]

interface ZeroExFill {
  source?: string
  proportionBps?: string | number
  proportion?: string | number
}

interface ZeroExResponse {
  buyAmount?: string
  sellAmount?: string
  minBuyAmount?: string
  gas?: string
  transaction?: { to?: string; data?: string; value?: string; gas?: string }
  issues?: { allowance?: { spender?: string } | null }
  route?: { fills?: ZeroExFill[] }
  totalNetworkFee?: string
  reason?: string
  validationErrors?: { reason?: string }[]
}

function extractRoute(response: ZeroExResponse): RouteHop[] {
  return (response.route?.fills ?? [])
    .map((fill) => {
      // v2 reports proportionBps; older payloads use a 0-1 proportion.
      const bps =
        fill.proportionBps !== undefined
          ? Number(fill.proportionBps)
          : Math.round(Number(fill.proportion ?? 0) * 10_000)
      return { source: fill.source ?? 'Unknown', portionBps: Number.isFinite(bps) ? bps : 0 }
    })
    .filter((hop) => hop.portionBps > 0)
}

export function normalise(response: ZeroExResponse, request: QuoteRequest): AggregatorQuote {
  if (response.reason || response.validationErrors?.length) {
    throw new Error(response.validationErrors?.[0]?.reason ?? response.reason ?? '0x rejected the quote')
  }
  if (!response.buyAmount) throw new Error('No route found')

  return {
    providerId: '0x',
    providerLabel: '0x',
    sellAmount: response.sellAmount ?? request.sellAmount,
    buyAmount: response.buyAmount,
    minBuyAmount: response.minBuyAmount ?? response.buyAmount,
    estimatedGas: response.transaction?.gas ?? response.gas,
    // In v2 the spender is reported under issues.allowance; it is not the same address
    // as the transaction target, so it must be read separately.
    allowanceTarget: response.issues?.allowance?.spender ?? response.transaction?.to,
    route: extractRoute(response),
    fee: revenueConfig.enabled
      ? { bps: revenueConfig.feeBps, recipient: revenueConfig.feeRecipient }
      : undefined,
    transaction: response.transaction?.to && response.transaction?.data
      ? {
          to: response.transaction.to,
          data: response.transaction.data,
          value: response.transaction.value ?? '0',
          gas: response.transaction.gas,
        }
      : undefined,
    raw: response,
  }
}

export const zeroExProvider: QuoteProvider = {
  id: '0x',
  label: '0x',

  supportsChain: (chainId) => SUPPORTED_CHAINS.includes(chainId),

  // The key lives on the proxy, so without a proxy this source cannot be used at all.
  isConfigured: () => hasQuoteProxy,

  async fetchQuote(request, signal) {
    const query = buildQuery({
      chainId: request.chainId,
      sellToken: request.sellToken.address,
      buyToken: request.buyToken.address,
      sellAmount: request.sellAmount,
      taker: request.taker,
      slippageBps: request.slippageBps,
      // 0x charges the fee in the buy token, which keeps it out of the user's
      // approval surface.
      swapFeeRecipient: revenueConfig.enabled ? revenueConfig.feeRecipient : undefined,
      swapFeeBps: revenueConfig.enabled ? revenueConfig.feeBps : undefined,
      swapFeeToken: revenueConfig.enabled ? request.buyToken.address : undefined,
    })

    const url = `${endpointFor('0x', DIRECT_BASE, '/swap/allowance-holder/quote')}?${query}`
    return normalise(await getJson<ZeroExResponse>(url, signal), request)
  },
}
